import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useOrg } from './OrgContext';

// The logo a company uploads for itself.
//
// This is not the app's logo. OnHand's own mark is drawn by
// components/brand/OnHandLogo.jsx, from code, and nobody can change it - that is
// the point of it being a product mark. What lives here is the customer's logo:
// the one an admin uploads under Settings so their own people recognise the
// screen as theirs.
//
// It is per-company, because app_settings is keyed on (org_id, key) and the
// database only ever hands back the row for the site you are currently in. The
// browser cache below has to be keyed the same way, or a person who works in
// two companies sees the wrong one flash on load.

const CACHE_PREFIX = 'onhand_company_logo:';

function cacheKey(orgId) {
  return CACHE_PREFIX + orgId;
}

// '' means "resolved: this company has no logo". A missing key means "never
// asked", which is what holds the login card's height steady on a first visit.
function readCached(orgId) {
  if (!orgId) return null;
  try { return localStorage.getItem(cacheKey(orgId)); } catch { return null; }
}

const EMPTY = {
  companyLogoUrl: null,
  companyName: '',
  resolved: false,
  refreshCompanyLogo: () => {},
};

const BrandingContext = createContext(EMPTY);

export function BrandingProvider({ children }) {
  const { current } = useOrg();
  const orgId = current?.id || null;

  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [resolved, setResolved] = useState(false);

  const fetchLogo = useCallback(async () => {
    // Before a site is known there is nothing to ask for. The login screen is
    // the ordinary case: it shows the OnHand lockup, which needs no query.
    if (!orgId) { setCompanyLogoUrl(null); setResolved(false); return; }

    const { data } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'logo_url')
      .maybeSingle();
    const val = data?.value;
    const url = (val && val !== 'EMPTY' && val !== '') ? val : null;
    setCompanyLogoUrl(url);
    setResolved(true);
    try { localStorage.setItem(cacheKey(orgId), url || ''); } catch { /* private mode */ }
  }, [orgId]);

  // Show the last-known logo for THIS site immediately, then confirm it. Keyed
  // by org, so switching sites shows the new company's logo or nothing at all -
  // never the previous company's.
  useEffect(() => {
    const cached = readCached(orgId);
    setCompanyLogoUrl(cached || null);
    setResolved(cached !== null);
    fetchLogo();
  }, [orgId, fetchLogo]);

  const value = useMemo(() => ({
    companyLogoUrl,
    companyName: current?.name || '',
    resolved,
    refreshCompanyLogo: fetchLogo,
  }), [companyLogoUrl, current, resolved, fetchLogo]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
